import { EventBus } from '../core/EventBus';
import type { CharacterLook } from '../state/types';
import {
  decodeState,
  encodeState,
  HEARTBEAT_MS,
  MAX_NEIGHBORS,
  parseDuel,
  parseFx,
  parseHello,
  parseSocial,
  parseStatus,
  PROTOCOL_VERSION,
  RateLimiter,
  sanitizeChat,
  STATE_INTERVAL_MS,
  STATUS_INTERVAL_MS,
  type DuelMsg,
  type FxMsg,
  type RemoteState,
  type SocialKind,
  type StatusMsg,
} from './protocol';

/**
 * The slice of a peer-to-peer room the session needs. The real one is backed by
 * Trystero (see trystero.ts); tests plug in an in-memory network.
 */
export interface Transport {
  /** Registers a named message channel and returns its sender (broadcast, or to one peer). */
  channel(name: string, onMessage: (data: unknown, peerId: string) => void): Sender;
  onPeerJoin(fn: (peerId: string) => void): void;
  onPeerLeave(fn: (peerId: string) => void): void;
  leave(): void;
}

export type Sender = (data: unknown, peerId?: string) => void;

export type TransportProblem = 'unreachable' | 'password';

/** Connects to the room for a park code. Rejects if the network layer can't load. */
export type TransportFactory = (
  code: string,
  onProblem: (problem: TransportProblem, peerId: string) => void,
) => Promise<Transport>;

export type SessionStatus = 'offline' | 'joining' | 'open';

export interface Neighbor {
  readonly id: string;
  readonly name: string;
  readonly look: CharacterLook;
  /** Their home lot (index into HOME_LOTS). */
  lot: number;
  readonly host: boolean;
  /** When they joined the park (ms since epoch). */
  readonly since: number;
  status: StatusMsg | null;
  /** No word from them in a while (tab in the background, or a bad connection). */
  away: boolean;
  heardAt: number;
}

export interface SessionEvents {
  status: SessionStatus;
  join: Neighbor;
  /** Status or away-ness changed. */
  update: Neighbor;
  leave: Neighbor;
  state: { from: Neighbor; state: RemoteState };
  chat: { from: Neighbor; text: string };
  social: { from: Neighbor; kind: SocialKind };
  fx: { from: Neighbor; fx: FxMsg };
  duel: { from: Neighbor; msg: DuelMsg };
  /** Something the player should hear about (as a sentence). */
  problem: string;
}

export interface Profile {
  name: string;
  look: CharacterLook;
  /** The home lot you claim. */
  lot: number;
  /** You opened this park. */
  host: boolean;
  status: StatusMsg;
}

export interface SessionOptions {
  connect: TransportFactory;
  profile: () => Profile;
  /** Milliseconds; defaults to performance.now. */
  now?: () => number;
  /** Wall clock for join times; defaults to Date.now. */
  wallClock?: () => number;
}

const AWAY_AFTER_MS = 6000;
const FX_INTERVAL_MS: Record<FxMsg['kind'], number> = {
  fireworks: 1500,
  tornado: 60_000,
  cops: 60_000,
  stunt: 3000,
  ufo: 60_000,
};

type ChannelName = 'hello' | 'st' | 'status' | 'chat' | 'social' | 'fx' | 'duel';

/** How often each kind of duel message may arrive (ms). */
const DUEL_INTERVAL_MS: Record<DuelMsg['t'], number> = {
  invite: 1500,
  accept: 300,
  decline: 300,
  cancel: 300,
  push: 40,
  arm: 40,
  end: 300,
  reveal: 300,
};

/**
 * One player's membership in a park: who's here, and validated, rate-limited
 * messages to and from them. Knows nothing about rendering.
 */
export class Session {
  readonly bus = new EventBus<SessionEvents>();
  status: SessionStatus = 'offline';
  code: string | null = null;
  /** When we joined the current park (ms since epoch). */
  since = 0;

  private transport: Transport | null = null;
  private send: Record<ChannelName, Sender> | null = null;
  private readonly peers = new Map<string, Neighbor>();
  /** Peers we've introduced ourselves to. */
  private readonly greeted = new Set<string>();
  private readonly warned = new Set<string>();
  private readonly limits = new RateLimiter();
  private generation = 0;
  private lastStateAt = -Infinity;
  private lastStateKey = '';
  private lastStatusAt = -Infinity;
  private lastChatAt = -Infinity;
  private readonly now: () => number;

  constructor(private readonly opts: SessionOptions) {
    this.now = opts.now ?? (() => performance.now());
  }

  get neighbors(): Neighbor[] {
    return [...this.peers.values()];
  }

  neighbor(id: string): Neighbor | undefined {
    return this.peers.get(id);
  }

  async join(code: string): Promise<boolean> {
    this.leave();
    const gen = ++this.generation;
    this.code = code;
    this.since = (this.opts.wallClock ?? Date.now)();
    this.setStatus('joining');
    let transport: Transport;
    try {
      transport = await this.opts.connect(code, (problem, peerId) =>
        this.onTransportProblem(problem, peerId),
      );
    } catch {
      if (gen !== this.generation) return false;
      this.code = null;
      this.setStatus('offline');
      this.bus.emit('problem', "Couldn't get online. Check your connection and try again.");
      return false;
    }
    if (gen !== this.generation) {
      transport.leave(); // left (or switched parks) while connecting
      return false;
    }
    this.transport = transport;
    const on = (name: ChannelName, fn: (data: unknown, peer: string) => void): Sender =>
      transport.channel(name, (data, peer) => {
        if (gen === this.generation) fn(data, peer);
      });
    this.send = {
      hello: on('hello', (d, p) => this.onHello(d, p)),
      st: on('st', (d, p) => this.onState(d, p)),
      status: on('status', (d, p) => this.onStatus(d, p)),
      chat: on('chat', (d, p) => this.onChat(d, p)),
      social: on('social', (d, p) => this.onSocial(d, p)),
      fx: on('fx', (d, p) => this.onFx(d, p)),
      duel: on('duel', (d, p) => this.onDuel(d, p)),
    };
    transport.onPeerJoin((peer) => gen === this.generation && this.greet(peer));
    transport.onPeerLeave((peer) => gen === this.generation && this.drop(peer));
    this.setStatus('open');
    return true;
  }

  leave(): void {
    this.generation++;
    const transport = this.transport;
    this.transport = null;
    this.send = null;
    transport?.leave();
    // Go offline before saying goodbye to everyone, so listeners can tell
    // "they left" apart from "we left".
    const wasOnline = this.status !== 'offline';
    this.status = 'offline';
    this.code = null;
    for (const id of [...this.peers.keys()]) this.drop(id);
    this.greeted.clear();
    this.warned.clear();
    this.lastStateKey = '';
    if (wasOnline) this.bus.emit('status', 'offline');
  }

  /** Call every frame: marks quiet neighbors as away and keeps our status fresh. */
  tick(): void {
    const now = this.now();
    for (const n of this.peers.values()) {
      if (!n.away && now - n.heardAt > AWAY_AFTER_MS) {
        n.away = true;
        this.bus.emit('update', n);
      }
    }
    if (this.send && now - this.lastStatusAt > STATUS_INTERVAL_MS) {
      this.lastStatusAt = now;
      this.send.status(this.opts.profile().status);
    }
  }

  /** Throttled to ~10 updates a second, and to a slow heartbeat while nothing changes. */
  sendState(state: RemoteState): void {
    if (!this.send || this.peers.size === 0) return;
    const now = this.now();
    if (now - this.lastStateAt < STATE_INTERVAL_MS) return;
    const encoded = encodeState(state);
    const key = encoded.slice(0, -1).join();
    if (key === this.lastStateKey && now - this.lastStateAt < HEARTBEAT_MS) return;
    this.lastStateAt = now;
    this.lastStateKey = key;
    this.send.st(encoded);
  }

  /** Returns the cleaned-up text that was sent, or null if nothing was (empty, or too fast). */
  chat(raw: string): string | null {
    const text = sanitizeChat(raw);
    const now = this.now();
    if (!text || !this.send || now - this.lastChatAt < 800) return null;
    this.lastChatAt = now;
    this.send.chat(text);
    return text;
  }

  social(peerId: string, kind: SocialKind): void {
    if (this.peers.has(peerId)) this.send?.social({ kind }, peerId);
  }

  fx(fx: FxMsg): void {
    if (this.peers.size) this.send?.fx(fx);
  }

  /** Arm wrestling and dice, one-on-one. */
  duel(peerId: string, msg: DuelMsg): void {
    if (this.peers.has(peerId)) this.send?.duel(msg, peerId);
  }

  /** Re-announces our status right away (we moved lots, bought decor...). */
  sendStatusNow(): void {
    if (!this.send) return;
    this.lastStatusAt = this.now();
    this.send.status(this.opts.profile().status);
  }

  // --- incoming ------------------------------------------------------------------------

  private setStatus(status: SessionStatus): void {
    this.status = status;
    this.bus.emit('status', status);
  }

  private greet(peer: string): void {
    if (!this.send || this.greeted.has(peer)) return;
    this.greeted.add(peer);
    const { name, look, lot, host, status } = this.opts.profile();
    this.send.hello({ v: PROTOCOL_VERSION, name, look, lot, host, since: this.since }, peer);
    this.send.status(status, peer);
  }

  private drop(peer: string): void {
    this.greeted.delete(peer);
    this.limits.forget(`${peer}:`);
    const n = this.peers.get(peer);
    if (!n) return;
    this.peers.delete(peer);
    this.bus.emit('leave', n);
  }

  /** A neighbor's message, if it isn't arriving faster than `minMs` after the last one. */
  private from(peer: string, channel: string, minMs: number): Neighbor | null {
    const n = this.peers.get(peer);
    if (!n || !this.limits.allow(`${peer}:${channel}`, this.now(), minMs)) return null;
    n.heardAt = this.now();
    if (n.away) {
      n.away = false;
      this.bus.emit('update', n);
    }
    return n;
  }

  private warnOnce(key: string, message: string): void {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    this.bus.emit('problem', message);
  }

  private onTransportProblem(problem: TransportProblem, peer: string): void {
    if (problem === 'unreachable')
      this.warnOnce(
        `net:${peer}`,
        "Couldn't connect to a neighbor. Some networks (school or work Wi-Fi, some phone carriers) block direct connections.",
      );
  }

  private onHello(raw: unknown, peer: string): void {
    // Names and looks are set once per visit; ignoring later hellos keeps a misbehaving
    // peer from churning through endless new outfits (each one costs a material).
    if (this.peers.has(peer)) return;
    const hello = parseHello(raw);
    if (hello === 'version') {
      this.warnOnce(
        'version',
        "A neighbor is on a different version of the game. Y'all both refresh the page.",
      );
      return;
    }
    if (!hello) return;
    if (this.peers.size >= MAX_NEIGHBORS) {
      this.warnOnce(
        'full',
        `This park is full (${MAX_NEIGHBORS + 1} players max). Some folks won't show up.`,
      );
      return;
    }
    const n: Neighbor = {
      id: peer,
      name: hello.name,
      look: hello.look,
      lot: hello.lot,
      host: hello.host,
      since: hello.since,
      status: null,
      away: false,
      heardAt: this.now(),
    };
    this.peers.set(peer, n);
    this.greet(peer); // in case they found us first
    this.bus.emit('join', n);
  }

  private onState(raw: unknown, peer: string): void {
    const n = this.from(peer, 'st', 25);
    const state = n && decodeState(raw);
    if (n && state) this.bus.emit('state', { from: n, state });
  }

  private onStatus(raw: unknown, peer: string): void {
    // Status changes right after a lot move, so allow a quick follow-up.
    const n = this.from(peer, 'status', 200);
    const status = n && parseStatus(raw);
    if (!n || !status) return;
    n.status = status;
    n.lot = status.lot;
    this.bus.emit('update', n);
  }

  private onChat(raw: unknown, peer: string): void {
    const n = this.from(peer, 'chat', 700);
    const text = n && sanitizeChat(raw);
    if (n && text) this.bus.emit('chat', { from: n, text });
  }

  private onSocial(raw: unknown, peer: string): void {
    const kind = parseSocial(raw);
    const n = kind && this.from(peer, `social:${kind}`, 2000);
    if (n && kind) this.bus.emit('social', { from: n, kind });
  }

  private onDuel(raw: unknown, peer: string): void {
    const msg = parseDuel(raw);
    const n = msg && this.from(peer, `duel:${msg.t}`, DUEL_INTERVAL_MS[msg.t]);
    if (n && msg) this.bus.emit('duel', { from: n, msg });
  }

  private onFx(raw: unknown, peer: string): void {
    const fx = parseFx(raw);
    const n = fx && this.from(peer, `fx:${fx.kind}`, FX_INTERVAL_MS[fx.kind]);
    if (n && fx) this.bus.emit('fx', { from: n, fx });
  }
}
